/**
 * @fileoverview Unlock Preferences Service - Manages user's unlock duration preferences
 *
 * Stores and retrieves user preferences for how long an unlock should remain valid.
 * Uses IndexedDB for persistence.
 *
 * @module services/UnlockPreferencesService
 */

import { createLogger, isRecord } from '@xanectech/lib';

const logger = createLogger('UnlockPreferencesService');

const DB_NAME = 'XanecTechUnlockPreferences';
const DB_VERSION = 1;
const STORE_NAME = 'preferences';

export interface UnlockDurationOption {
  id: string;
  label: string;
  durationMs: number | null; // null = never expire
  description: string;
  warningLevel: 'none' | 'warning' | 'critical'; // warning level for UI
}

export const UNLOCK_DURATION_OPTIONS: UnlockDurationOption[] = [
  {
    id: 'fifteen-min',
    label: '15 minutes',
    durationMs: 15 * 60 * 1000,
    description: 'Shortest duration - maximum security',
    warningLevel: 'none',
  },
  {
    id: 'thirty-min',
    label: '30 minutes',
    durationMs: 30 * 60 * 1000,
    description: 'Recommended for shared devices',
    warningLevel: 'none',
  },
  {
    id: 'one-hour',
    label: '1 hour',
    durationMs: 60 * 60 * 1000,
    description: 'Recommended for personal devices',
    warningLevel: 'none',
  },
  {
    id: 'eight-hours',
    label: '8 hours',
    durationMs: 8 * 60 * 60 * 1000,
    description: 'Full work day - for trusted devices only',
    warningLevel: 'warning',
  },
  {
    id: 'twenty-four-hours',
    label: '24 hours',
    durationMs: 24 * 60 * 60 * 1000,
    description: 'Full day - high risk on shared devices',
    warningLevel: 'warning',
  },
  {
    id: 'never-expire',
    label: 'Never expire',
    durationMs: null,
    description:
      'Permanently unlocked until logout - ONLY for trusted personal devices',
    warningLevel: 'critical',
  },
];

interface PreferenceRecord {
  userId: string;
  durationOptionId: string;
  durationMs: number | null;
  updatedAt: number;
}

interface UnlockStatusRecord {
  userId: string;
  isUnlocked: boolean;
  expiresAt?: number | null;
}

const parseUnlockStatusRecord = (value: unknown): UnlockStatusRecord | null => {
  if (!isRecord(value)) {
    return null;
  }

  if (typeof value.userId !== 'string') {
    return null;
  }

  return {
    userId: value.userId,
    isUnlocked: Boolean(value.isUnlocked),
    expiresAt:
      typeof value.expiresAt === 'number' || value.expiresAt === null
        ? value.expiresAt
        : undefined,
  };
};

const getRequestError = (event: Event): unknown => {
  const target = event.target;
  if (target && 'error' in target) {
    return (target as { error?: unknown }).error;
  }
  return undefined;
};

/**
 * Initialize IndexedDB for preferences
 */
async function initDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(new Error('Failed to open preferences DB'));
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'userId' });
      }
    };
  });
}

/**
 * Set user's unlock duration preference
 * Also updates the current unlock expiry if user is currently unlocked
 */
export async function setUnlockDurationPreference(
  userId: string,
  durationOptionId: string,
): Promise<void> {
  try {
    const option = UNLOCK_DURATION_OPTIONS.find(
      (o) => o.id === durationOptionId,
    );
    if (!option) {
      throw new Error(`Invalid duration option: ${durationOptionId}`);
    }

    const db = await initDB();
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    const record: PreferenceRecord = {
      userId,
      durationOptionId,
      durationMs: option.durationMs,
      updatedAt: Date.now(),
    };

    await new Promise<void>((resolve, reject) => {
      const request = store.put(record);
      request.onsuccess = async () => {
        db.close();
        logger.info('Unlock duration preference set', {
          userId,
          durationOptionId,
        });
        // Update the current unlock record with new expiry and await completion
        await updateUnlockExpiryWithNewPreference(userId, option.durationMs);
        resolve();
      };
      request.onerror = () => {
        db.close();
        reject(new Error('Failed to set unlock duration preference'));
      };
    });
  } catch (error) {
    logger.error('Error setting unlock duration preference', error as Error);
    throw error;
  }
}

/**
 * Update the current unlock record's expiry time with the new preference duration
 * Can be called by other services to sync unlock expiry with preferences
 */
export async function updateUnlockExpiryWithNewPreference(
  userId: string,
  newDurationMs: number | null,
): Promise<void> {
  try {
    const db = await new Promise<IDBDatabase>((resolve, reject) => {
      const request = indexedDB.open('XanecTechUnlockStatus', 1);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () =>
        reject(new Error('Failed to open unlock status DB'));
    });

    return new Promise<void>((resolve, reject) => {
      try {
        const transaction = db.transaction(['unlock_status'], 'readwrite');
        const store = transaction.objectStore('unlock_status');

        // Use getAll to find the unlock record (userId is the keyPath)
        const getAllRequest = store.getAll();

        getAllRequest.onsuccess = () => {
          try {
            const records = Array.isArray(getAllRequest.result)
              ? getAllRequest.result
              : [];

            const record = records
              .map(parseUnlockStatusRecord)
              .find((r): r is UnlockStatusRecord => !!r && r.userId === userId);

            if (record && record.isUnlocked) {
              // Update expiry with new preference
              const now = Date.now();
              const newExpiresAt =
                newDurationMs === null ? null : now + newDurationMs;

              const updatedRecord = {
                ...record,
                expiresAt: newExpiresAt,
              };


              const putRequest = store.put(updatedRecord);
              putRequest.onsuccess = () => {
                db.close();
                logger.info('Successfully updated unlock expiry', {
                  userId,
                  newDurationMs,
                  newExpiresAt,
                });
                resolve();
              };
              putRequest.onerror = (event: Event) => {
                db.close();
                const error = getRequestError(event);
                logger.warn('Failed to update unlock expiry', {
                  userId,
                  error:
                    error instanceof Error ? error : new Error(String(error)),
                });
                resolve(); // Don't reject - this is non-critical
              };
            } else {
              db.close();
              resolve();
            }
          } catch (error) {
            db.close();
            logger.error('Error in getAllRequest callback', error as Error);
            resolve();
          }
        };

        getAllRequest.onerror = (event: Event) => {
          db.close();
          const error = getRequestError(event);
          logger.warn('Failed to fetch unlock records for expiry update', {
            userId,
            error: error instanceof Error ? error : new Error(String(error)),
          });
          resolve(); // Don't reject - this is non-critical
        };
      } catch (error) {
        db.close();
        logger.error('Error setting up transaction', error as Error);
        resolve();
      }
    });
  } catch (error) {
    logger.error(
      'Error updating unlock expiry with new preference',
      error as Error,
    );
    // Don't throw - this is a non-critical operation
  }
}

/**
 * Get user's unlock duration preference
 * @returns Duration in milliseconds, or null for never expire
 */
export async function getUnlockDurationPreference(
  userId: string,
): Promise<number | null> {
  try {
    const db = await initDB();
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);

    return new Promise<number | null>((resolve, reject) => {
      const request = store.get(userId);
      request.onsuccess = () => {
        const record = request.result as PreferenceRecord | undefined;
        db.close();

        if (!record) {
          // Default to 1 hour if no preference set
          resolve(60 * 60 * 1000);
          return;
        }

        resolve(record.durationMs);
      };
      request.onerror = () => {
        db.close();
        reject(new Error('Failed to get unlock duration preference'));
      };
    });
  } catch (error) {
    logger.error('Error getting unlock duration preference', error as Error);
    return 60 * 60 * 1000; // Default to 1 hour on error
  }
}

/**
 * Get the option label for the user's current preference
 */
export async function getUnlockDurationLabel(userId: string): Promise<string> {
  try {
    const db = await initDB();
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);

    return new Promise<string>((resolve, reject) => {
      const request = store.get(userId);
      request.onsuccess = () => {
        const record = request.result as PreferenceRecord | undefined;
        db.close();

        if (!record) {
          resolve('1 hour');
          return;
        }

        const option = UNLOCK_DURATION_OPTIONS.find(
          (o) => o.id === record.durationOptionId,
        );
        resolve(option?.label || '1 hour');
      };
      request.onerror = () => {
        db.close();
        resolve('1 hour');
      };
    });
  } catch (error) {
    return '1 hour';
  }
}

/**
 * Update active unlock record expiry based on preference option ID
 * Used by UserPreferencesService to sync unlock state when expiry preference changes
 */
export async function updateUnlockExpiryWithPreference(
  userId: string,
  durationOptionId: string,
): Promise<void> {
  const option = UNLOCK_DURATION_OPTIONS.find((o) => o.id === durationOptionId);
  if (!option) {
    logger.warn('Invalid duration option', { durationOptionId });
    return;
  }

  await updateUnlockExpiryWithNewPreference(userId, option.durationMs);
}
